import React, { useState } from 'react'
import {
  atom,
  AtomEffect,
  CachePolicyWithoutEquality,
  CallbackInterface,
  constSelector,
  DefaultValue,
  ReadOnlySelectorFamilyOptions,
  ReadOnlySelectorOptions,
  RecoilValueReadOnly,
  selector,
  selectorFamily,
  SerializableParam,
  useRecoilCallback,
  useRecoilValueLoadable,
} from 'recoil'
import { IS_SERVER } from 'src/config'
import { browserLogger } from 'src/utils/browser-logger'

// We need have at least a couple of cached version to have multiples similar widgets on the Report
export const DEFAULT_LRU_SIZE = 100
const DEBUG_RECOIL_SELECTORS = !IS_SERVER && window && window.location.toString().includes('debug-recoil')

const allKeys: string[] = []

function debugSelectorCall<T>(options: SelectorOptions<T>) {
  return (props: any) => {
    browserLogger.debug('Selector Get', options.key)
    return options.get(props)
  }
}

const isStorybook = () => {
  if (typeof process === 'undefined') {
    return false
  }
  if (process.env.STORYBOOK) {
    return true
  }
  return false
}

export const keyFactory = (prefix: string): ((rest: string) => string) => {
  return (rest: string) => {
    const key = `${prefix}-${rest}`
    if (process.env.NODE_ENV !== 'production' || !isStorybook()) {
      if (allKeys.includes(key)) {
        throw new Error('Duplicate recoil key detected. This causes horrible problems. Here is the key: ' + key)
      }
      allKeys.push(key)
    }
    return key
  }
}

export function useCachedRecoilValue<T>(
  selector: RecoilValueReadOnly<T>,
  defaultValue: T,
  condition?: () => boolean
): T {
  const loadable = useRecoilValueLoadable(condition && !condition() ? constSelector(null) : selector)
  const valueRef = React.useRef<T>(defaultValue)
  if (loadable.state === 'hasError') {
    const msg = `recoil error for ${selector.key}:`
    browserLogger.error(msg, loadable.contents)
    throw loadable.contents
  }
  if (loadable.state !== 'loading') {
    const value = loadable.valueOrThrow()
    if (value) {
      valueRef.current = value
    } else {
      valueRef.current = defaultValue
    }
  }
  return valueRef.current
}

// this atom is incremented when user requests retry of loading async data
// it is used to cache bust selectors which have rejected
export const retryAtom = atom<number>({
  key: 'retry-atom',
  default: 0,
})

export const localStorageEffect =
  <T>(key: string): AtomEffect<T> =>
  ({ setSelf, onSet }) => {
    try {
      const savedValue = localStorage.getItem(key)
      if (savedValue != null) {
        setSelf(JSON.parse(savedValue))
      }
    } catch (e) {
      console.error(e)
    }

    onSet((newValue) => {
      try {
        if (newValue instanceof DefaultValue) {
          localStorage.removeItem(key)
        } else {
          localStorage.setItem(key, JSON.stringify(newValue))
        }
      } catch (e) {
        console.error(e)
      }
    })
  }

type UncachedSelectorOptions = {
  lru?: number
}

type SelectorOptions<T> = ReadOnlySelectorOptions<T> & UncachedSelectorOptions

function getCachePolicy(options: UncachedSelectorOptions): CachePolicyWithoutEquality {
  return options.lru ? { eviction: 'lru', maxSize: options.lru } : { eviction: 'most-recent' }
}

export function uncachedSelector<T>(options: SelectorOptions<T>): RecoilValueReadOnly<T> {
  return selector({
    ...options,
    cachePolicy_UNSTABLE: options.cachePolicy_UNSTABLE ?? getCachePolicy(options),
    get: DEBUG_RECOIL_SELECTORS ? debugSelectorCall(options) : options.get,
  })
}

type SelectorFamilyOptions<T, P extends SerializableParam> = ReadOnlySelectorFamilyOptions<T, P> &
  UncachedSelectorOptions

export function uncachedSelectorFamily<T, P extends SerializableParam>(
  options: SelectorFamilyOptions<T, P>
): (param: P) => RecoilValueReadOnly<T> {
  return selectorFamily({
    ...options,
    cachePolicy_UNSTABLE: options.cachePolicy_UNSTABLE ?? getCachePolicy(options),
  })
}

type MockableSelectorOptions<T> = SelectorOptions<T> & {
  mock: RecoilValueReadOnly<T | undefined>
}

// create a selector which can be mocked with fake data for storybooks
// in production there is no mocking supported, and its just a regular selector
export function mockableSelector<T>(options: MockableSelectorOptions<T>): RecoilValueReadOnly<T> {
  return selector({
    ...options,
    get: isStorybook()
      ? ({ get, ...rest }) => {
          const val = get(options.mock)
          if (val) {
            return val
          }
          return options.get({ get, ...rest })
        }
      : options.get,
  })
}

export type UseRecoilMutationOptions<TData> = {
  onSuccess?: (data: TData) => void
  onError?: (error: unknown) => void
}

/** Currently, this hook only supports local states.
 * If you need a global loading or error state, you will need to handle it in the function you define
 * */
export const useRecoilMutation = <Args extends ReadonlyArray<unknown>, TData>(
  fn: (cbInterface: CallbackInterface) => (...args: Args) => Promise<TData>,
  deps?: ReadonlyArray<unknown>,
  options?: UseRecoilMutationOptions<TData>
) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState<string>()
  const mutation = useRecoilCallback<Args, Promise<TData>>(fn, deps)

  const performMutation = (
    onSuccessCb?: (result: TData) => void,
    onErrorCb?: (err: unknown) => void,
    ...args: Args
  ) => {
    setIsLoading(true)
    setError(undefined)
    setIsSuccess(false)
    mutation(...args)
      .then((data) => {
        setIsSuccess(true)
        options?.onSuccess?.(data)
        onSuccessCb?.(data)
      })
      .catch((err) => {
        setError((err as Error).message ?? 'Somethig went wrong')
        options?.onError?.(err)
        onErrorCb?.(err)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const mutate = (...args: Args) => {
    performMutation(undefined, undefined, ...args)
  }

  const mutateAsync = (...args: Args) => {
    return new Promise<TData>((resolve, reject) => {
      performMutation(
        (data) => resolve(data),
        (err) => reject(err),
        ...args
      )
    })
  }

  return {
    isLoading,
    isSuccess,
    error,
    mutate,
    mutateAsync,
  }
}
